import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, DirectionsRenderer, Marker } from '@react-google-maps/api';
import { Navigation, Clock, Loader2 } from 'lucide-react';

const libraries = ['places', 'geometry'];

const RouteMap = ({ 
  pickup, 
  dropoff, 
  distance: propDistance, 
  durationMinutes: propDuration,
  height = '200px',
  showInfo = true 
}) => {
  const [directions, setDirections] = useState(null);
  const [error, setError] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 41.0082, lng: 28.9784 }); // Istanbul default
  const [routeDistance, setRouteDistance] = useState(null);
  const [routeDuration, setRouteDuration] = useState(null);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey || '',
    libraries,
  });

  // Dark map style matching GoTurkeyTransfer theme
  const mapStyles = useMemo(() => [
    { elementType: 'geometry', stylers: [{ color: '#212121' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] },
    { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#757575' }] },
    { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] },
    { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#3c3c3c' }] },
    { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f2f2f' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] },
  ], []);

  const mapOptions = useMemo(() => ({
    styles: mapStyles,
    disableDefaultUI: true,
    zoomControl: false,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    gestureHandling: 'cooperative',
  }), [mapStyles]);

  // Route line style - orange color matching brand
  const directionsOptions = useMemo(() => ({
    suppressMarkers: true, // We'll add custom markers
    polylineOptions: {
      strokeColor: '#FF8C00',
      strokeWeight: 4,
      strokeOpacity: 0.9,
    },
  }), []);

  // Calculate directions when component mounts or locations change
  const calculateRoute = useCallback(async () => {
    if (!isLoaded || !pickup || !dropoff) return;
    
    try {
      const directionsService = new window.google.maps.DirectionsService();
      
      const result = await directionsService.route({
        origin: pickup,
        destination: dropoff,
        travelMode: window.google.maps.TravelMode.DRIVING,
      });
      
      setDirections(result);
      setError(null);
      
      // Extract distance and duration from the route
      if (result.routes[0]?.legs[0]) {
        const leg = result.routes[0].legs[0];
        // Distance in km (leg.distance.value is in meters)
        const distanceKm = Math.round(leg.distance.value / 1000);
        // Duration in minutes (leg.duration.value is in seconds)
        const durationMins = Math.round(leg.duration.value / 60);
        
        setRouteDistance(distanceKm);
        setRouteDuration(durationMins);
      }
      
      // Get center point of the route
      if (result.routes[0]?.bounds) {
        const bounds = result.routes[0].bounds;
        setMapCenter({
          lat: (bounds.getNorthEast().lat() + bounds.getSouthWest().lat()) / 2,
          lng: (bounds.getNorthEast().lng() + bounds.getSouthWest().lng()) / 2,
        });
      }
    } catch (err) {
      console.error('Directions request failed:', err);
      setError('Could not calculate route');
      
      // Try to geocode just the pickup for a fallback center
      try {
        const geocoder = new window.google.maps.Geocoder();
        const result = await geocoder.geocode({ address: pickup });
        if (result.results[0]?.geometry?.location) {
          setMapCenter({
            lat: result.results[0].geometry.location.lat(),
            lng: result.results[0].geometry.location.lng(),
          });
        }
      } catch (geocodeErr) {
        console.error('Geocode failed:', geocodeErr);
      }
    }
  }, [isLoaded, pickup, dropoff]);

  useEffect(() => {
    calculateRoute();
  }, [calculateRoute]);

  // Custom marker icon for pickup (A)
  const pickupMarker = useMemo(() => {
    if (!isLoaded || !directions) return null;
    const route = directions.routes[0];
    if (!route?.legs[0]) return null;
    
    return {
      position: route.legs[0].start_location,
      label: { text: 'A', color: 'white', fontWeight: 'bold' },
    };
  }, [isLoaded, directions]);

  // Custom marker icon for dropoff (B)
  const dropoffMarker = useMemo(() => {
    if (!isLoaded || !directions) return null;
    const route = directions.routes[0];
    if (!route?.legs[0]) return null;
    
    return {
      position: route.legs[0].end_location,
      label: { text: 'B', color: 'white', fontWeight: 'bold' },
    };
  }, [isLoaded, directions]);

  if (loadError) {
    return (
      <div className="bg-gray-900 flex items-center justify-center" style={{ height }}>
        <p className="text-gray-500 text-sm">Failed to load map</p>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="bg-gray-900 flex items-center justify-center" style={{ height }}>
        <Loader2 className="h-6 w-6 animate-spin text-[#FF8C00]" />
      </div>
    );
  }

  return (
    <div className="relative" style={{ height }}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={mapCenter}
        zoom={11}
        options={mapOptions}
      >
        {/* Route polyline */}
        {directions && (
          <DirectionsRenderer 
            directions={directions} 
            options={directionsOptions}
          />
        )}
        
        {/* Custom markers */}
        {pickupMarker && (
          <Marker
            position={pickupMarker.position}
            label={pickupMarker.label}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: '#FF8C00',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            }}
          />
        )}
        
        {dropoffMarker && (
          <Marker
            position={dropoffMarker.position}
            label={dropoffMarker.label}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 12,
              fillColor: '#FF8C00',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2,
            }}
          />
        )}
      </GoogleMap>
      
      {/* Route info overlay */}
      {showInfo && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4">
          <div className="flex items-center gap-4 text-white text-sm">
            <div className="flex items-center gap-1">
              <Navigation className="h-4 w-4 text-[#FF8C00]" />
              <span>{routeDistance || propDistance || '—'} km</span>
            </div>
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4 text-[#FF8C00]" />
              <span>~{routeDuration || propDuration || '—'} min</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Error fallback */}
      {error && !directions && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80">
          <div className="text-center text-white">
            <Navigation className="h-8 w-8 mx-auto mb-2 text-[#FF8C00]" />
            <p className="text-xs text-gray-400">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteMap;
